import { Fragment, useState, useEffect, useRef } from 'react';
import { Trash2, Edit2, Check, X, FileText, Play } from 'react-feather';
import * as tus from 'tus-js-client';
import { useRemote } from '../../Utils/Utils';
import Page, { PageContentBodyGrid } from '../Shared/Page/Page';
import PageGridItem from '../Shared/Page/PageGridItem';
import LoadingIcon from '../../components/LoadingIcon';

const getDescription = (video) =>
	(video?.metaTags || []).find(t => (t.property || '').toLowerCase() === 'description')?.value || '';

const BunnyTestPage = () => {
	const remote = useRemote();

	const [videos, setVideos] = useState([]);
	const [loading, setLoading] = useState(true);

	// Upload form state
	const [title, setTitle] = useState('');
	const [file, setFile] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [uploadError, setUploadError] = useState(null);
	const [uploadProgress, setUploadProgress] = useState(0);
	const uploadRef = useRef(null);

	// Per-video transcription trigger state — 'pending' | 'done' | 'error'
	const [transcribeState, setTranscribeState] = useState({});

	// Captions cache — { [guid]: { loading, error, cues } }
	const [captions, setCaptions] = useState({});

	// Details modal state
	const [detailsGuid, setDetailsGuid] = useState(null);
	const [editing, setEditing] = useState(false);
	const [editTitle, setEditTitle] = useState('');
	const [editDescription, setEditDescription] = useState('');
	const [saveConfirm, setSaveConfirm] = useState(false);

	// Delete inline confirm — videoId currently armed for delete on the card
	const [deleteConfirmId, setDeleteConfirmId] = useState(null);

	const detailsVideo = detailsGuid && videos.find(v => v.guid === detailsGuid);

	const fetchVideos = () => {
		setLoading(true);
		remote.get('bunny/videos').then(res => {
			const items = res?.data?.items || res?.items || [];
			setVideos(items);
			setLoading(false);
		}).catch(() => setLoading(false));
	};

	useEffect(fetchVideos, []);

	// Abort an in-flight upload if the page unmounts.
	useEffect(() => () => { if (uploadRef.current) uploadRef.current.abort(); }, []);

	// Upload directly to Bunny via TUS: mint a signed token, then stream the file
	// straight to video.bunnycdn.com (bypasses our server — no relay/hang on large files).
	const handleUpload = () => {
		if (!title.trim() || !file) return;
		setUploading(true);
		setUploadError(null);
		setUploadProgress(0);

		remote.post(`bunny/videos/tus-init?title=${encodeURIComponent(title.trim())}`, {})
			.then(res => {
				const creds = res?.data ?? res;
				if (!creds || !creds.videoId) throw new Error('init failed');
				const upload = new tus.Upload(file, {
					endpoint: creds.tusEndpoint,
					retryDelays: [0, 3000, 5000, 10000, 20000],
					chunkSize: 50 * 1024 * 1024,
					removeFingerprintOnSuccess: true,
					headers: {
						AuthorizationSignature: creds.signature,
						AuthorizationExpire: String(creds.expiration),
						VideoId: creds.videoId,
						LibraryId: String(creds.libraryId),
					},
					metadata: { filetype: file.type, title: title.trim() },
					onError: () => {
						uploadRef.current = null;
						remote.post(`bunny/videos/${creds.videoId}/upload-complete`, { success: false, sizeBytes: file.size, title: title.trim(), error: 'upload failed' }).catch(() => { /* non-fatal */ });
						setUploadError('Upload failed'); setUploading(false);
					},
					onProgress: (sent, total) => { if (total > 0) setUploadProgress(Math.round((sent / total) * 100)); },
					onSuccess: () => {
						uploadRef.current = null;
						remote.post(`bunny/videos/${creds.videoId}/upload-complete`, { success: true, sizeBytes: file.size, title: title.trim() }).catch(() => { /* non-fatal */ });
						setTitle(''); setFile(null); setUploading(false); fetchVideos();
					},
				});
				uploadRef.current = upload;
				upload.start();
			})
			.catch(() => { setUploadError('Upload failed'); setUploading(false); });
	};

	const openDetails = (video) => {
		setDetailsGuid(video.guid);
		setEditing(false);
		setSaveConfirm(false);
		setEditTitle(video.title || '');
		setEditDescription(getDescription(video));
		if (!captions[video.guid]) {
			setCaptions(c => ({ ...c, [video.guid]: { loading: true } }));
			remote.get(`bunny/videos/${video.guid}/transcription`)
				.then(res => {
					const cues = res?.data || res || [];
					setCaptions(c => ({ ...c, [video.guid]: { loading: false, cues } }));
				})
				.catch(() => setCaptions(c => ({ ...c, [video.guid]: { loading: false, error: true } })));
		}
	};

	const closeDetails = () => {
		setDetailsGuid(null);
		setEditing(false);
		setSaveConfirm(false);
	};

	const handleSave = () => {
		if (!detailsVideo) return;
		const payload = {
			title: editTitle,
			metaTags: [{ property: 'description', value: editDescription }],
		};
		remote.post(`bunny/videos/${detailsVideo.guid}`, payload).then(res => {
			if (res && res.success) {
				setEditing(false);
				setSaveConfirm(false);
				fetchVideos();
			}
		});
	};

	const handleDelete = (videoId) => {
		remote.delete(`bunny/videos/${videoId}`).then(() => {
			setDeleteConfirmId(null);
			if (detailsGuid === videoId) closeDetails();
			fetchVideos();
		});
	};

	const handleTranscribe = (videoId) => {
		setTranscribeState(s => ({ ...s, [videoId]: 'pending' }));
		remote.post(`bunny/videos/${videoId}/transcribe`, {})
			.then(res => {
				setTranscribeState(s => ({ ...s, [videoId]: res && res.success ? 'done' : 'error' }));
			})
			.catch(() => setTranscribeState(s => ({ ...s, [videoId]: 'error' })));
	};

	const detailsState = detailsVideo ? (captions[detailsVideo.guid] || {}) : {};
	const detailsCues = detailsState.cues || [];
	const detailsTranscribe = detailsVideo ? transcribeState[detailsVideo.guid] : undefined;

	return (
		<Page noBanner>
			<PageContentBodyGrid rowGap="xl">

				{/* Upload Form */}
				<PageGridItem colSpan="12">
					<h2 style={{ marginBottom: 16 }}>Bunny Video Test</h2>
					<div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
						<input
							type="text"
							placeholder="Video title"
							value={title}
							onChange={e => setTitle(e.target.value)}
							style={{ padding: '8px 12px', borderRadius: 4, border: '1px solid #ccc', minWidth: 220 }}
						/>
						<input
							type="file"
							accept="video/*"
							onChange={e => setFile(e.target.files[0] || null)}
							style={{ padding: '6px 0' }}
						/>
						<button
							onClick={handleUpload}
							disabled={uploading || !title.trim() || !file}
							style={{
								padding: '8px 18px', borderRadius: 4, border: 'none',
								background: uploading || !title.trim() || !file ? '#aaa' : '#0E8EFD',
								color: '#fff', cursor: uploading || !title.trim() || !file ? 'not-allowed' : 'pointer'
							}}
						>
							{uploading ? `Uploading… ${uploadProgress}%` : 'Upload'}
						</button>
					</div>
					{uploadError && <p style={{ color: 'red', marginTop: 8 }}>{uploadError}</p>}
				</PageGridItem>

				{/* Video Grid */}
				<PageGridItem colSpan="12">
					{loading
						? <LoadingIcon />
						: videos.length === 0
							? <p style={{ color: '#666' }}>No videos found.</p>
							: (
								<div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
									{videos.map(video => (
										<div key={video.guid} style={{
											border: '1px solid #e0e0e0', borderRadius: 8,
											overflow: 'hidden', background: '#fff',
											boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
											cursor: 'pointer'
										}}
											onClick={() => openDetails(video)}
											title="View details"
										>
											<div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', background: '#111', overflow: 'hidden' }}>
												<img
													src={`/api/bunny/videos/${video.guid}/thumbnail`}
													alt={video.title}
													style={{ width: '100%', height: '100%', objectFit: 'cover' }}
													onError={e => { e.target.style.display = 'none'; }}
												/>
												<div style={{
													position: 'absolute', inset: 0, display: 'flex',
													alignItems: 'center', justifyContent: 'center',
													background: 'rgba(0,0,0,0.25)'
												}}>
													<Play size={36} color="#fff" fill="#fff" />
												</div>
											</div>

											<div style={{ padding: 12 }}>
												<p style={{ fontWeight: 600, margin: '0 0 4px', fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
													{video.title || '(no title)'}
												</p>
												<p style={{ fontSize: 12, color: '#888', margin: '0 0 8px' }}>
													Status: {video.status}
													&nbsp;|&nbsp;Views: {video.views ?? 0}
													&nbsp;|&nbsp;{Math.round((video.storageSize || 0) / 1024 / 1024)} MB
												</p>
												<div style={{ display: 'flex', gap: 8, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
													{deleteConfirmId === video.guid ? (
														<>
															<span style={{ fontSize: 12, color: '#e53' }}>Delete?</span>
															<button onClick={() => handleDelete(video.guid)} title="Confirm delete"
																style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e53', display: 'flex' }}>
																<Check size={15} />
															</button>
															<button onClick={() => setDeleteConfirmId(null)} title="Cancel"
																style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', display: 'flex' }}>
																<X size={15} />
															</button>
														</>
													) : (
														<button onClick={() => setDeleteConfirmId(video.guid)} title="Delete"
															style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e53', display: 'flex' }}>
															<Trash2 size={15} />
														</button>
													)}
												</div>
											</div>
										</div>
									))}
								</div>
							)
					}
				</PageGridItem>

			</PageContentBodyGrid>

			{/* Details modal */}
			{detailsVideo && (
				<div
					style={{
						position: 'fixed', inset: 0, zIndex: 1100,
						background: 'rgba(0,0,0,0.6)',
						display: 'flex', alignItems: 'center', justifyContent: 'center',
						padding: 20
					}}
					onClick={e => { if (e.target === e.currentTarget) closeDetails(); }}
				>
					<div style={{ background: '#fff', borderRadius: 8, width: '80vw', maxWidth: 900, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
						{/* Header */}
						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #eee', gap: 12 }}>
							{editing ? (
								<input
									type="text"
									value={editTitle}
									onChange={e => setEditTitle(e.target.value)}
									placeholder="Title"
									style={{ flex: 1, padding: '6px 10px', borderRadius: 4, border: '1px solid #ccc', fontSize: 15, fontWeight: 600 }}
								/>
							) : (
								<strong style={{ fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
									{detailsVideo.title || '(no title)'}
								</strong>
							)}
							<button onClick={closeDetails} title="Close"
								style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex' }}>
								<X size={18} />
							</button>
						</div>

						{/* Body */}
						<div style={{ padding: '14px 18px', overflowY: 'auto' }}>
							{/* Embedded player */}
							<div style={{ position: 'relative', paddingTop: '56.25%', marginBottom: 12, background: '#000', borderRadius: 4, overflow: 'hidden' }}>
								<iframe
									key={detailsVideo.guid}
									src={`https://player.mediadelivery.net/embed/${detailsVideo.videoLibraryId}/${detailsVideo.guid}`}
									loading="lazy"
									style={{ border: 'none', position: 'absolute', top: 0, height: '100%', width: '100%' }}
									allow="accelerometer; gyroscope; encrypted-media; picture-in-picture;"
									allowFullScreen
								/>
							</div>

							{/* Metadata strip */}
							<p style={{ fontSize: 12, color: '#888', margin: '0 0 14px' }}>
								Status: {detailsVideo.status}
								&nbsp;|&nbsp;Views: {detailsVideo.views ?? 0}
								&nbsp;|&nbsp;{Math.round((detailsVideo.storageSize || 0) / 1024 / 1024)} MB
								{detailsVideo.length ? <>&nbsp;|&nbsp;{detailsVideo.length}s</> : null}
							</p>

							{/* Description */}
							<div style={{ marginBottom: 14 }}>
								<label style={{ display: 'block', fontSize: 12, color: '#555', fontWeight: 600, marginBottom: 4 }}>Description</label>
								{editing ? (
									<textarea
										value={editDescription}
										onChange={e => setEditDescription(e.target.value)}
										placeholder="Description"
										rows={5}
										style={{ width: '100%', padding: '8px 10px', borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box', fontSize: 13, lineHeight: 1.5, fontFamily: 'inherit', resize: 'vertical' }}
									/>
								) : (
									<p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: getDescription(detailsVideo) ? '#222' : '#888', whiteSpace: 'pre-wrap' }}>
										{getDescription(detailsVideo) || '(no description)'}
									</p>
								)}
							</div>

							{/* Action row */}
							<div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18 }}>
								{editing ? (
									saveConfirm ? (
										<>
											<span style={{ fontSize: 13, color: '#0E8EFD' }}>Save changes?</span>
											<button onClick={handleSave} title="Confirm save"
												style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0E8EFD', display: 'flex' }}>
												<Check size={16} />
											</button>
											<button onClick={() => setSaveConfirm(false)} title="Cancel"
												style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', display: 'flex' }}>
												<X size={16} />
											</button>
										</>
									) : (
										<>
											<button onClick={() => setSaveConfirm(true)}
												style={{ padding: '6px 14px', borderRadius: 4, border: '1px solid #0E8EFD', background: '#0E8EFD', color: '#fff', cursor: 'pointer', fontSize: 13 }}>
												Save
											</button>
											<button onClick={() => {
												setEditing(false);
												setSaveConfirm(false);
												setEditTitle(detailsVideo.title || '');
												setEditDescription(getDescription(detailsVideo));
											}}
												style={{ padding: '6px 14px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', color: '#555', cursor: 'pointer', fontSize: 13 }}>
												Cancel
											</button>
										</>
									)
								) : (
									<button onClick={() => setEditing(true)}
										style={{ padding: '6px 14px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', color: '#555', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
										<Edit2 size={14} /> Edit
									</button>
								)}
								<button
									onClick={() => handleTranscribe(detailsVideo.guid)}
									disabled={detailsTranscribe === 'pending'}
									title={
										detailsTranscribe === 'pending' ? 'Triggering…'
										: detailsTranscribe === 'done' ? 'Transcription triggered'
										: detailsTranscribe === 'error' ? 'Transcribe failed — click to retry'
										: 'Transcribe'
									}
									style={{
										padding: '6px 14px', borderRadius: 4,
										border: '1px solid #ccc', background: '#fff',
										color: detailsTranscribe === 'done' ? '#2ca44b'
											: detailsTranscribe === 'error' ? '#e53'
											: '#555',
										cursor: detailsTranscribe === 'pending' ? 'wait' : 'pointer',
										fontSize: 13, display: 'flex', alignItems: 'center', gap: 6
									}}>
									<FileText size={14} /> Transcribe
								</button>
							</div>

							{/* Captions */}
							<div>
								<label style={{ display: 'block', fontSize: 12, color: '#555', fontWeight: 600, marginBottom: 6 }}>Captions</label>
								{detailsState.loading ? <LoadingIcon />
									: detailsState.error ? <p style={{ color: 'red', fontSize: 13 }}>Failed to load captions.</p>
									: detailsCues.length === 0 ? <p style={{ color: '#888', fontSize: 13 }}>No captions yet. Trigger transcription first.</p>
									: (
										<div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', columnGap: 14, rowGap: 8, fontSize: 13, lineHeight: 1.5 }}>
											{detailsCues.map((cue, i) => (
												<Fragment key={i}>
													<span style={{ color: '#888', fontFamily: 'monospace', fontSize: 12 }}>
														{cue.start ? cue.start.slice(0, 8) : ''}
													</span>
													<span>{cue.text}</span>
												</Fragment>
											))}
										</div>
									)
								}
							</div>
						</div>
					</div>
				</div>
			)}
		</Page>
	);
};

export default BunnyTestPage;
